import React, { ReactNode, useState } from 'react';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../primitives/dialog';
import { DataTableWithControl } from './DataTable';
import { ColumnDef, PaginationState } from '@tanstack/react-table';
import { Checkbox } from '../primitives/checkbox';
import { BaseUser, Role } from '~/util/types/users';
import {
    ClanTags,
    ClanTagsProps,
    LevelGPTags,
    LevelTags,
    PermissionsTags,
    TagsBase,
    TimezoneTags,
    UserTags,
    UserTagsProps,
} from '../partials/Tags';
import { getLevelDisplayPrefix, ModalProps } from '~/util';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../primitives/button';
import { levelsQuery } from '~/util/queries/public/levels';
import type { BaseLevel } from '~/util/types/levels';
import { classicTags, platTags, Tag } from '~/util/types/tags';
import { Country } from 'iso-3166-1/dist/iso-3166';
import Flag from '~/partials/Flag';
import { ScrollArea } from '~/primitives/scroll-area';
import ApiErrorComponent from '~/partials/ApiErrorComponent';
import { Clan } from '~/util/types/clans';
import { getCountryList, getCountryName } from '~/util/countries';
import { useList } from '~/util/hooks/useList';
import { rolesQuery, usersQuery } from '~/util/queries/public/users';
import { clansQuery } from '~/util/queries/public/clans';
import { useScopes } from './Providers/AuthProvider';
import { useApiMutation } from '~/util/hooks/useApiMutation';
import { createUserMutation } from '~/util/mutations/staff/users';
import { Plus } from 'lucide-react';
import { useResettableStateWithKey } from '~/util/hooks/useResettableState';
import {
    Permission,
    permissionDescriptions,
    permissions,
} from '~/util/types/permissions';
import { getTimeZones, TimeZone } from '@vvo/tzdb';

type SingleSelectorBaseProps<T> = {
    initial: T | undefined;
    single: true;
    onSave: (value: T | undefined) => void;
};

type MultiSelectorBaseProps<T> = {
    initial: T[];
    single?: false;
    onSave: (value: T[]) => void;
};

type SelectorBaseProps<T> = ModalProps &
    (SingleSelectorBaseProps<T> | MultiSelectorBaseProps<T>) & {
        additionalDescription?: string;
    };

type ItemSelectorProps<T> = SelectorBaseProps<T> & {
    idFn: (item: T) => string;
    items: T[];
    itemColumn: ColumnDef<T>;
    title: string;
    renderTags?: (items: T[], onRemove: (item: T) => void) => ReactNode;
    noResultsFallback?: (
        search: string | undefined,
        afterCreate: (item: T) => void,
    ) => ReactNode;

    totalPages?: number;
    enableInternalPagination?: boolean;
    enableInternalFiltering?: boolean;
    search?: string;
    alwaysShowNoResultsFallback?: boolean;
    onSearchChange?: (value: string) => void;
    pagination?: PaginationState;
    onPageChange?: React.Dispatch<React.SetStateAction<PaginationState>>;
    isLoading?: boolean;
    showPaginationDisplay?: boolean;
    disableAllPagination?: boolean;
};

const checkboxColumn = <T,>(): ColumnDef<T> => ({
    id: 'select',
    cell: ({ row }) => (
        <div className="h-full flex items-center justify-center">
            <Checkbox checked={row.getIsSelected()} />
        </div>
    ),
});

const ItemSelector = <T,>(props: ItemSelectorProps<T>) => {
    const {
        idFn,
        items,
        itemColumn,
        title,
        renderTags,
        search,
        onSearchChange,
        enableInternalFiltering,
        enableInternalPagination,
        noResultsFallback,
        alwaysShowNoResultsFallback,
        pagination,
        additionalDescription,
        onPageChange,
        totalPages,
        isLoading,
        ...modalProps
    } = props;

    const [selected, setSelected] = useResettableStateWithKey(
        props.initial,
        () =>
            Array.isArray(props.initial)
                ? props.initial
                : props.initial
                  ? [props.initial]
                  : [],
    );

    return (
        <Dialog {...modalProps}>
            <DialogContent
                aria-describedby={
                    additionalDescription ? 'dialog-description' : undefined
                }
                disableDialogScroll
            >
                <DialogHeader className="pl-3">
                    <DialogTitle>{title}</DialogTitle>
                    {additionalDescription && (
                        <DialogDescription>
                            {additionalDescription}
                        </DialogDescription>
                    )}
                </DialogHeader>
                <ScrollArea className="min-h-0 flex-1 mr-1 pr-3">
                    <DataTableWithControl
                        isLoading={isLoading}
                        columns={[checkboxColumn<T>(), itemColumn]}
                        data={items}
                        onRowClick={({ data, row }) => {
                            setSelected((s) => {
                                if (!row.getIsSelected()) {
                                    return props.single
                                        ? [data]
                                        : s.concat(data);
                                } else {
                                    return props.single
                                        ? []
                                        : s.filter(
                                              (e) => idFn(e) !== idFn(data),
                                          );
                                }
                            });
                        }}
                        idFn={idFn}
                        multiselect={!props.single}
                        selected={selected}
                        enableInternalFiltering={enableInternalFiltering}
                        enableInternalPagination={enableInternalPagination}
                        search={search}
                        onSearchChange={onSearchChange}
                        pagination={pagination}
                        onPageChange={onPageChange}
                        totalPages={totalPages}
                        cellClassName={() => 'p-2 lg:p-4'}
                        headerClassName={() => 'p-2 lg:p-4'}
                        noResultsFallback={noResultsFallback}
                        alwaysShowNoResultsFallback={
                            alwaysShowNoResultsFallback
                        }
                    />
                </ScrollArea>
                {renderTags && (
                    <div className="ml-6 py-2">
                        {renderTags(selected, (item) =>
                            setSelected((s) =>
                                s.filter((e) => idFn(e) !== idFn(item)),
                            ),
                        )}
                    </div>
                )}

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="secondary">Cancel</Button>
                    </DialogClose>
                    <DialogClose asChild>
                        <Button
                            onClick={() => {
                                if (props.single) {
                                    props.onSave(selected?.[0] ?? undefined);
                                } else {
                                    props.onSave(selected);
                                }
                            }}
                        >
                            Save
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

type UserSelectorProps = SelectorBaseProps<BaseUser> & {
    onlyPlaceholder?: boolean;
    onlyVerified?: boolean;
    tagOptions?: Omit<UserTagsProps, 'item' | 'items' | 'disableClick'>;
};

export const UserSelector: React.FC<UserSelectorProps> = (props) => {
    const { onlyPlaceholder, onlyVerified, tagOptions, ...selectorProps } =
        props;
    const single = selectorProps.single;
    const [search, setSearch] = useState('');
    const [page, setPage] = useState({ pageIndex: 0, pageSize: 5 });
    const {
        data: usersResponse,
        isPending,
        isError,
        error,
    } = useQuery(
        usersQuery({
            page: page.pageIndex + 1,
            per_page: page.pageSize,
            name_filter: search,
            placeholder_filter: onlyPlaceholder
                ? true
                : onlyVerified
                  ? false
                  : undefined,
        }),
    );

    const { hasScope } = useScopes();

    const mutation = useApiMutation(createUserMutation());

    if (isError) return <ApiErrorComponent {...error} />;

    return (
        <ItemSelector
            idFn={(user) => user.id}
            isLoading={isPending}
            items={usersResponse?.data ?? []}
            itemColumn={{
                accessorKey: 'global_name',
                header: 'Name',
                cell: ({ row: { original: user } }) => (
                    <UserTags
                        className="bg-transparent text-secondary-foreground"
                        disableClick
                        highContrast
                        item={user}
                        {...tagOptions}
                    />
                ),
            }}
            title={`Select user${single ? '' : 's'}`}
            renderTags={(items, onRemove) => (
                <div className="flex items-center gap-2">
                    {`Selected user${single ? '' : 's'}: `}
                    {items?.length > 0 ? (
                        <UserTags
                            items={items}
                            highContrast
                            disableClick
                            onRemove={onRemove}
                            {...tagOptions}
                        />
                    ) : (
                        'None'
                    )}
                </div>
            )}
            search={search}
            onSearchChange={setSearch}
            pagination={page}
            onPageChange={setPage}
            totalPages={usersResponse?.pages}
            noResultsFallback={
                hasScope('user_modify')
                    ? (search) => (
                          <Button
                              variant="outline"
                              className="gap-2 h-fit mt-2"
                              onClick={() =>
                                  mutation.mutate({ global_name: search })
                              }
                          >
                              <Plus className="h-8 w-8" />{' '}
                              <div className="flex flex-col">
                                  <p>Create placeholder user:</p>
                                  <p className="italic">{search}</p>
                              </div>
                          </Button>
                      )
                    : undefined
            }
            alwaysShowNoResultsFallback={hasScope('user_modify')}
            {...selectorProps}
        />
    );
};

type ClanSelectorProps = SelectorBaseProps<Clan> & {
    tagOptions?: Omit<ClanTagsProps, 'item' | 'items' | 'disableClick'>;
};

export const ClanSelector: React.FC<ClanSelectorProps> = (props) => {
    const { tagOptions, ...selectorProps } = props;
    const single = props.single;

    const [search, setSearch] = useState('');
    const [page, setPage] = useState({ pageIndex: 0, pageSize: 20 });
    const {
        data: clansResponse,
        isPending,
        isError,
        error,
    } = useQuery(
        clansQuery({
            page: page.pageIndex + 1,
            per_page: page.pageSize,
            name_filter: search,
        }),
    );

    if (isError) return <ApiErrorComponent {...error} />;

    return (
        <ItemSelector
            isLoading={isPending}
            items={clansResponse?.data ?? []}
            idFn={(clan) => clan.id}
            itemColumn={{
                accessorKey: 'global_name',
                header: 'Name',
                cell: ({ row: { original: clan } }) => (
                    <ClanTags
                        className="bg-transparent text-secondary-foreground"
                        disableClick
                        highContrast
                        item={clan}
                        {...tagOptions}
                    />
                ),
            }}
            title={`Select clan${single ? '' : 's'}`}
            renderTags={(items, onRemove) => (
                <div className="flex items-center gap-2">
                    {`Selected clan${single ? '' : 's'}: `}
                    {items?.length > 0 ? (
                        <ClanTags
                            disableClick
                            highContrast
                            items={items}
                            onRemove={onRemove}
                            {...tagOptions}
                        />
                    ) : (
                        'None'
                    )}
                </div>
            )}
            search={search}
            onSearchChange={setSearch}
            pagination={page}
            onPageChange={setPage}
            totalPages={clansResponse?.pages}
            {...selectorProps}
        />
    );
};

type TagSelectorProps = SelectorBaseProps<Tag> & {
    tagOptions?: Omit<TagsBase<Tag>, 'item' | 'items' | 'disableClick'>;
};
export const TagsSelector: React.FC<TagSelectorProps> = (props) => {
    const list = useList();
    const { tagOptions, ...selectorProps } = props;
    const single = props.single;

    return (
        <ItemSelector<Tag>
            items={
                (list == 'classic' ? classicTags : platTags) as unknown as Tag[]
            }
            title={`Select Tag${single ? '' : 's'}`}
            idFn={(tag) => tag}
            itemColumn={{
                accessorKey: 'tag',
                header: 'Tag',
                cell: ({ row: { original: tag } }) => (
                    <LevelGPTags
                        item={tag}
                        highContrast
                        disableClick
                        {...tagOptions}
                    />
                ),
            }}
            renderTags={(items, onRemove) => (
                <div className="flex items-center gap-2">
                    {`Selected tag${single ? '' : 's'}: `}
                    {items?.length > 0 ? (
                        <LevelGPTags
                            highContrast
                            disableClick
                            items={items}
                            onRemove={onRemove}
                            {...tagOptions}
                        />
                    ) : (
                        'None'
                    )}
                </div>
            )}
            noResultsFallback={(search) => (
                <p className="italic text-gray-500">
                    No tags found matching &quot;{search}&quot;
                </p>
            )}
            {...selectorProps}
        />
    );
};

export const CountrySelector: React.FC<SelectorBaseProps<Country>> = (
    props,
) => {
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 100,
    });
    const [search, setSearch] = useState('');
    const filteredCountries = getCountryList().filter((country) =>
        getCountryName(country).toLowerCase().includes(search.toLowerCase()),
    );

    return (
        <ItemSelector<Country>
            idFn={(country) => country.numeric}
            items={filteredCountries}
            itemColumn={{
                accessorKey: 'Country',
                header: 'Country',
                cell: ({ row: { original: country } }) => (
                    <div className="flex items-center space-x-2">
                        <Flag
                            className="h-8 w-8"
                            countryCode={country.numeric}
                        />
                        <p>{getCountryName(country)}</p>
                    </div>
                ),
            }}
            title={`Select Country`}
            renderTags={(items, onRemove) => (
                <div className="inline-flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2">
                        {`Selected countr${props.single ? 'y' : 'ies'}: `}
                        {items?.length > 0
                            ? items?.map(
                                  (country) =>
                                      country && (
                                          <div
                                              key={country.alpha3}
                                              className="flex items-center gap-1 px-2 py-1 rounded-sm"
                                          >
                                              <Flag
                                                  countryCode={country.numeric}
                                              />
                                              <p>{getCountryName(country)}</p>
                                              {onRemove && (
                                                  <button
                                                      onClick={() =>
                                                          onRemove(country)
                                                      }
                                                      className="ml-1 text-secondary-light hover:text-secondary-foreground"
                                                  >
                                                      &times;
                                                  </button>
                                              )}
                                          </div>
                                      ),
                              )
                            : 'None'}
                    </div>
                </div>
            )}
            onPageChange={setPagination}
            pagination={pagination}
            enableInternalFiltering
            enableInternalPagination
            search={search}
            onSearchChange={setSearch}
            {...props}
        />
    );
};

type LevelsSelectorProps = SelectorBaseProps<BaseLevel> & {
    list?: 'classic' | 'platformer';
    customLevels?: BaseLevel[];
    tagOptions?: Omit<TagsBase<BaseLevel>, 'item' | 'items' | 'disableClick'>;
};

export const LevelsSelector: React.FC<LevelsSelectorProps> = (props) => {
    const { list, customLevels, tagOptions, ...selectorProps } = props;
    const single = selectorProps.single;

    const [search, setSearch] = useState('');
    const [page, setPage] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 100,
    });
    const {
        data: levels,
        isPending,
        isError,
        error,
    } = useQuery(
        levelsQuery({
            list: list ?? 'classic',
            exclude_legacy: true,
            exclude_pending: false,
        }),
    );
    if (isError) return <div>Error: {error.message}</div>;
    return (
        <ItemSelector<BaseLevel>
            idFn={(level) => level.id}
            items={customLevels ? customLevels : (levels ?? [])}
            isLoading={isPending}
            enableInternalFiltering
            enableInternalPagination
            pagination={page}
            onPageChange={setPage}
            search={search}
            onSearchChange={setSearch}
            title={`Select Level${single ? '' : 's'}`}
            itemColumn={{
                accessorKey: 'name',
                header: 'Level',
                cell: ({ row }) => (
                    <div className="flex items-center gap-2">
                        <p className="text-secondary-light">
                            {getLevelDisplayPrefix(row.original)}
                        </p>
                        <p>{row.original.name}</p>
                    </div>
                ),
            }}
            renderTags={(items, onRemove) => (
                <div className="flex items-center gap-2">
                    {`Selected level${single ? '' : 's'}: `}
                    {items?.length > 0 ? (
                        <LevelTags
                            className="text-secondary-foreground"
                            items={items}
                            onRemove={onRemove as (item: BaseLevel) => void}
                            disableClick
                            {...tagOptions}
                        />
                    ) : (
                        'None'
                    )}
                </div>
            )}
            {...selectorProps}
        />
    );
};

export const RolesSelector: React.FC<SelectorBaseProps<Role>> = (props) => {
    const single = props.single;

    const { data: roles, isPending, isError, error } = useQuery(rolesQuery());
    if (isError) return <div>Error: {error.message}</div>;
    return (
        <ItemSelector<Role>
            idFn={(role) => String(role.id)}
            items={
                roles?.sort((a, b) => b.privilege_level - a.privilege_level) ??
                []
            }
            isLoading={isPending}
            title={`Select Role${single ? '' : 's'}`}
            itemColumn={{
                accessorKey: 'role_desc',
                header: 'Role',
                cell: ({ row }) => (
                    <div className="flex items-center gap-2">
                        <p className="text-secondary-foreground bg-transparent">
                            {row.original.role_desc}
                        </p>
                    </div>
                ),
            }}
            {...props}
        />
    );
};

type PermissionSelectorProps = SelectorBaseProps<Permission> & {
    tagOptions?: Omit<TagsBase<Permission>, 'item' | 'items' | 'disableClick'>;
};
export const PermissionSelector: React.FC<PermissionSelectorProps> = (
    props,
) => {
    const { tagOptions, ...selectorProps } = props;
    const single = props.single;

    return (
        <ItemSelector<Permission>
            items={permissions as unknown as Permission[]}
            title={`Select Permission${single ? '' : 's'}`}
            idFn={(tag) => tag}
            itemColumn={{
                accessorKey: 'permission',
                header: 'Permission',
                cell: ({ row: { original: permission } }) => (
                    <>
                        <PermissionsTags
                            item={permission}
                            highContrast
                            disableClick
                            {...tagOptions}
                        />
                        <p className="text-secondary-light text-sm">
                            {permissionDescriptions[permission]}
                        </p>
                    </>
                ),
            }}
            renderTags={(items, onRemove) => (
                <div className="flex items-center gap-2">
                    {`Selected tag${single ? '' : 's'}: `}
                    {items?.length > 0 ? (
                        <PermissionsTags
                            highContrast
                            disableClick
                            items={items}
                            onRemove={onRemove}
                            {...tagOptions}
                        />
                    ) : (
                        'None'
                    )}
                </div>
            )}
            noResultsFallback={(search) => (
                <p className="italic text-gray-500">
                    No permissions found matching &quot;{search}&quot;
                </p>
            )}
            {...selectorProps}
        />
    );
};

type TimezoneSelectorProps = SelectorBaseProps<TimeZone> & {
    tagOptions?: Omit<TagsBase<TimeZone>, 'item' | 'items' | 'disableClick'>;
};

export const TimezoneSelector: React.FC<TimezoneSelectorProps> = (props) => {
    const { tagOptions, ...selectorProps } = props;
    const single = props.single;
    const [search, setSearch] = useState('');
    const timezones = getTimeZones({ includeUtc: true }).filter(
        (tz) =>
            tz.name.toLowerCase().includes(search.trim().toLowerCase()) ||
            tz.alternativeName
                .toLowerCase()
                .includes(search.trim().toLowerCase()) ||
            tz.abbreviation.toLowerCase().includes(search.trim().toLowerCase()),
    );

    return (
        <ItemSelector<TimeZone>
            items={timezones}
            title={`Select Timezone${single ? '' : 's'}`}
            idFn={(tz) => {
                console.log(tz);
                return tz.name;
            }}
            itemColumn={{
                accessorFn: (timezone) => timezone,
                header: 'Timezone',
                cell: ({ row: { original: timezone } }) => (
                    <>
                        <TimezoneTags
                            item={timezone}
                            highContrast
                            disableClick
                            {...tagOptions}
                        />
                    </>
                ),
            }}
            renderTags={(items, onRemove) => (
                <div className="flex items-center gap-2">
                    {`Selected timezone${single ? '' : 's'}`}:
                    {items?.length > 0 ? (
                        <TimezoneTags
                            highContrast
                            disableClick
                            items={items}
                            onRemove={onRemove}
                            {...tagOptions}
                        />
                    ) : (
                        'None'
                    )}
                </div>
            )}
            noResultsFallback={(search) => (
                <p className="italic text-gray-500">
                    No timezones found matching &quot;{search}&quot;
                </p>
            )}
            enableInternalFiltering={true}
            search={search}
            onSearchChange={setSearch}
            {...selectorProps}
        />
    );
};
